import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Clock, TrendingUp, Loader2 } from "lucide-react";
import { format } from "date-fns";
import ReactMarkdown from "react-markdown";
import SocialShareButtons from "@/components/SocialShareButtons";
import OpenGraphMeta from "@/components/OpenGraphMeta";

export default function ArticleView() {
  const { id } = useParams();
  const [article, setArticle] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadArticle();
  }, [id]);

  const loadArticle = async () => {
    try {
      const { data, error } = await supabase
        .from('articles')
        .select('*')
        .eq('id', id)
        .maybeSingle();

      if (error) throw error;
      setArticle(data);
    } catch (error) {
      console.error('Failed to load article:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5">
        <div className="container mx-auto px-4 py-12 max-w-4xl">
          <Link to="/blog-generator">
            <Button variant="ghost" size="sm" className="mb-4">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Generator
            </Button>
          </Link>
          <Card className="p-8 text-center">
            <h2 className="text-2xl font-bold mb-2">Article Not Found</h2>
            <p className="text-muted-foreground">This article doesn't exist or has been removed.</p>
          </Card>
        </div>
      </div>
    );
  }

  const shareUrl = `${window.location.origin}/article/${article.id}`;
  const hashtags = article.tokens_featured?.map((t: string) => t.replace(/[^a-zA-Z0-9]/g, '')) || ['crypto', 'aussiecrypto'];

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5">
      <OpenGraphMeta
        title={article.seo_title || article.headline}
        description={article.seo_description || article.deck || article.summary || ''}
        url={shareUrl}
        type="article"
        publishedTime={article.publish_date}
        tags={article.seo_keywords || []}
      />
      <div className="container mx-auto px-4 py-12 max-w-4xl">
        <Link to="/blog-generator">
          <Button variant="ghost" size="sm" className="mb-6">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Generator
          </Button>
        </Link>

        <article className="space-y-6">
          {/* Header */}
          <Card className="p-8 gradient-card border-border/50">
            <div className="space-y-4">
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="default" className="bg-gradient-primary">
                  {article.category}
                </Badge>
                {article.primary_token && (
                  <Badge variant="outline" className="border-primary/30">
                    <TrendingUp className="w-3 h-3 mr-1" />
                    {article.primary_token}
                  </Badge>
                )}
                {article.tokens_featured?.slice(1).map((token: string, idx: number) => (
                  <Badge key={idx} variant="outline" className="border-border/50">
                    {token}
                  </Badge>
                ))}
                <Badge variant="outline" className="ml-auto capitalize">
                  {article.status}
                </Badge>
              </div>

              <h1 className="text-4xl font-bold text-foreground leading-tight">
                {article.headline}
              </h1>

              {article.deck && (
                <p className="text-xl text-muted-foreground leading-relaxed">
                  {article.deck}
                </p>
              )}

              <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground pt-4 border-t border-border/30">
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  <span>{format(new Date(article.publish_date), 'MMM dd, yyyy')}</span>
                </div>
                <span>•</span>
                <span>{article.word_count || 0} words</span>
                <span>•</span>
                <span>{article.reading_time || 0} min read</span>
                {article.opportunity_score && (
                  <>
                    <span>•</span>
                    <span className="font-semibold text-primary">
                      Score: {article.opportunity_score}
                    </span>
                  </>
                )}
              </div>

              {/* Social Share Buttons - Top */}
              <div className="pt-4 border-t border-border/30">
                <SocialShareButtons
                  url={shareUrl}
                  title={article.headline}
                  description={article.deck || article.summary || ''}
                  hashtags={hashtags}
                  showLabel={true}
                  size="md"
                  articleId={article.id}
                />
              </div>
            </div>
          </Card>

          {/* Content */}
          <Card className="p-8 bg-card/50 backdrop-blur-sm">
            <div className="prose prose-lg max-w-none dark:prose-invert prose-headings:text-foreground prose-p:text-foreground/90 prose-strong:text-foreground prose-a:text-primary hover:prose-a:text-primary/80 prose-ul:text-foreground/90 prose-ol:text-foreground/90 prose-li:text-foreground/90">
              <ReactMarkdown
                components={{
                  h1: ({ children }) => <h1 className="text-3xl font-bold mb-4 mt-8">{children}</h1>,
                  h2: ({ children }) => <h2 className="text-2xl font-bold mb-3 mt-6">{children}</h2>,
                  h3: ({ children }) => <h3 className="text-xl font-semibold mb-2 mt-4">{children}</h3>,
                  p: ({ children }) => <p className="mb-4 leading-relaxed">{children}</p>,
                  ul: ({ children }) => <ul className="mb-4 ml-6 list-disc space-y-2">{children}</ul>,
                  ol: ({ children }) => <ol className="mb-4 ml-6 list-decimal space-y-2">{children}</ol>,
                  li: ({ children }) => <li className="leading-relaxed">{children}</li>,
                  strong: ({ children }) => <strong className="font-bold text-foreground">{children}</strong>,
                  em: ({ children }) => <em className="italic">{children}</em>,
                  a: ({ href, children }) => (
                    <a 
                      href={href} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-primary hover:text-primary/80 underline font-medium"
                    >
                      {children}
                    </a>
                  ),
                  blockquote: ({ children }) => (
                    <blockquote className="border-l-4 border-primary/50 pl-4 italic my-4 text-muted-foreground">
                      {children}
                    </blockquote>
                  ),
                }}
              >
                {article.content}
              </ReactMarkdown>
            </div>
          </Card>

          {/* Social Share Buttons - Bottom */}
          <div className="mt-8 pt-6 border-t border-border/30">
            <p className="text-sm text-muted-foreground mb-3">
              Found this useful? Share with your crypto community!
            </p>
            <SocialShareButtons
              url={shareUrl}
              title={article.headline}
              description={article.deck || article.summary || ''}
              hashtags={hashtags}
              showLabel={false}
              size="lg"
              articleId={article.id}
            />
          </div>

          {/* Footer Metadata */}
          {(article.seo_keywords || article.ai_recommendation) && (
            <Card className="p-6 bg-card/30 backdrop-blur-sm">
              {article.seo_keywords && article.seo_keywords.length > 0 && (
                <div className="mb-4">
                  <h3 className="text-sm font-semibold mb-2 text-muted-foreground">Keywords</h3>
                  <div className="flex flex-wrap gap-2">
                    {article.seo_keywords.map((keyword: string, idx: number) => (
                      <Badge key={idx} variant="secondary" className="text-xs">
                        {keyword}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {article.ai_recommendation && (
                <div>
                  <h3 className="text-sm font-semibold mb-2 text-muted-foreground">AI Recommendation</h3>
                  <p className="text-sm text-foreground/80">{article.ai_recommendation}</p>
                </div>
              )}
            </Card>
          )}
        </article>
      </div>

      {/* Floating Share Button */}
    </div>
  );
}
